import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import apiClient from "@/app/api/client";
import type { HrStatus } from "@/modules/employees/types";
import { usePaginatedQuery } from "@/shared/hooks/usePaginatedQuery";
import { getErrorMessage } from "@/shared/utils/getErrorMessage";
import { tasksService } from "../services/tasks.service";
import type { Task, TaskCreateInput, TaskListFilters, TaskUpdateInput } from "../types";

/**
 * ⚠️ Bo'sh/`undefined` qiymatlar params'ga TUSHMAYDI. Aks holda axios
 * ularni `?status=undefined` deb yuborardi va `TaskListQuerySchema`
 * (`z.enum`) 422 qaytarardi — «filtrni tozaladim» amali sahifani
 * butunlay sindirardi.
 */
export function buildTaskListParams(filters: TaskListFilters): Record<string, string> {
	const params: Record<string, string> = {};

	if (filters.status) {
		params.status = filters.status;
	}
	if (filters.employeeId) {
		params.employeeId = filters.employeeId;
	}
	if (filters.q?.trim()) {
		params.q = filters.q.trim();
	}
	if (filters.departmentId) {
		params.departmentId = filters.departmentId;
	}
	if (filters.assigneeDepartmentId) {
		params.assigneeDepartmentId = filters.assigneeDepartmentId;
	}
	// ⚠️ SATR (`"true"`) sifatida va FAQAT yoqilganda — backend `z.stringbool()`
	// bilan qabul qiladi. Bo'lim filtrisiz backend uni baribir e'tiborsiz
	// qoldiradi, shuning uchun u holda umuman uzatilmaydi.
	if ((filters.departmentId || filters.assigneeDepartmentId) && filters.includeDescendants) {
		params.includeDescendants = "true";
	}

	return params;
}

export function useTasksList(filters: TaskListFilters) {
	return usePaginatedQuery<Task>({
		queryKey: ["tasks"],
		url: "/tasks",
		params: buildTaskListParams(filters),
	});
}

export function useTaskDetail(id: string | undefined) {
	return useQuery({
		queryKey: ["tasks", "detail", id],
		queryFn: () => tasksService.detail(id as string),
		enabled: Boolean(id),
	});
}

export interface EmployeeOption {
	id: string;
	fullName: string;
	/** B2: «Barchasini tanlash» arxivlangan/ishdan bo'shagan xodimni chetlab o'tishi uchun. */
	hrStatus: HrStatus;
}

/**
 * ⚠️ **`limit` HAR DOIM 100 — API kontrakti** (A1/B1, 14.09.2026 fix
 * to'lqini). Ilgari bu yerda `departmentId ? 200 : 100` bor edi — spec
 * §4.2 ning eski yozuvidan ko'chirilgan, lekin backend
 * `EmployeeListQuerySchema.limit` MAKSIMUMI 100
 * (`employees.schemas.ts:47`, `z.coerce.number().int().min(1).max(100)`).
 * `GET /employees?departmentId=…&limit=200` shu sabab 422 qaytarardi va
 * bo'lim tanlangan blokda xodimlar ro'yxati DOIM bo'sh, "Barchasini
 * tanlash" esa hech qachon yoqilmasdi. Bo'limda 100 dan ortiq xodim
 * bo'lsa `total > items.length` ogohlantirishi (`OrgAssigneePicker`)
 * ishlab, foydalanuvchiga faqat birinchi 100 tanlanganini bildiradi —
 * pastga tushirish EMAS, TO'G'RI chegara.
 *
 * Sof funksiya sifatida ajratilgan — `buildTaskListParams` naqshi:
 * `apiClient` stub'siz, to'g'ridan-to'g'ri test qilinadi.
 */
export function buildEmployeeOptionsParams(
	search: string,
	departmentId: string | undefined
): Record<string, string | number> {
	const params: Record<string, string | number> = {
		page: 1,
		limit: 100,
	};

	if (search.trim()) {
		params.q = search.trim();
	}

	if (departmentId) {
		params.departmentId = departmentId;
	}

	return params;
}

/**
 * Xodim tanlash — server tomonida qidiriladi. Bank miqyosida xodimlar
 * 100 tadan ko'p, ya'ni bitta sahifani mijozda filtrlash yetarli emas.
 *
 * ⚠️ Boshqa modulning hook'i import QILINMAYDI (`useZones.ts` dagi
 * bir xil qaror): kichik so'rov `apiClient` orqali shu modulda
 * e'lon qilinadi. Modullararo import blok chegarasini ham buzardi.
 *
 * ⚠️ `departmentId` berilmasa so'rov UMUMAN yuborilmaydi
 * (`OrgAssigneePicker` naqshi, spec §4): tashkilot tanlanmagan blokda
 * "barcha xodimlar" ro'yxati mantiqsiz — foydalanuvchi avval bo'lim
 * tanlashi kerak.
 */
export function useEmployeeOptions(search: string, departmentId?: string) {
	return useQuery({
		queryKey: ["employees", "task-options", search, departmentId],
		queryFn: async () => {
			const params = buildEmployeeOptionsParams(search, departmentId);

			const response = await apiClient.get<{
				success: true;
				// `total` — «Barchasini tanlash» 100 chegarasidan oshganini bilish uchun.
				data: { items: EmployeeOption[]; meta: { total: number } };
			}>("/employees", { params });

			return { items: response.data.data.items, total: response.data.data.meta.total };
		},
		enabled: Boolean(departmentId),
		staleTime: 30_000,
	});
}

function useTasksMutation<TVariables, TResult>(
	mutationFn: (variables: TVariables) => Promise<TResult>,
	successMessage: string,
	errorFallback: string,
	/**
	 * B6 (14.09.2026 fix to'lqini): qo'shimcha keshlarni ham eskirtirish
	 * uchun — hozircha faqat `useCreateTask` `["departments", "summary"]`
	 * prefiksini beradi (profil chiplari yaratishdan keyin eskirmasin).
	 */
	extraInvalidateKeys: unknown[][] = []
) {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn,
		onSuccess: () => {
			// Ro'yxat ham, tafsilot ham eskiradi — ikkalasi bitta prefiks
			// ostida, ya'ni bitta invalidatsiya yetarli.
			queryClient.invalidateQueries({ queryKey: ["tasks"] });
			for (const queryKey of extraInvalidateKeys) {
				queryClient.invalidateQueries({ queryKey });
			}
			message.success(successMessage);
		},
		onError: (error) => {
			message.error(getErrorMessage(error, errorFallback));
		},
	});
}

/**
 * ⚠️ **`["departments", "summary"]` HAM eskirtiriladi** (B6, 14.09.2026 fix
 * to'lqini): bo'lim profilining "Topshiriqlar" tabi
 * (`DepartmentTasksTab`) `tasksByStatus` chiplarini shu kesh prefiksidan
 * o'qiydi (`useDepartmentSummary`) — yaratishdan keyin faqat `["tasks"]`
 * invalidatsiya qilinsa, yangi topshiriq chiplarda darhol ko'rinmasdi.
 */
export function useCreateTask() {
	const { t } = useTranslation("tasks");
	return useTasksMutation(
		(input: TaskCreateInput) => tasksService.create(input),
		t("messages.created"),
		t("messages.createFailed"),
		[["departments", "summary"]]
	);
}

export function useUpdateTask(id: string) {
	const { t } = useTranslation("tasks");
	return useTasksMutation(
		(input: TaskUpdateInput) => tasksService.update(id, input),
		t("messages.updated"),
		t("messages.updateFailed")
	);
}

export function useCancelTask() {
	const { t } = useTranslation("tasks");
	return useTasksMutation(
		(id: string) => tasksService.cancel(id),
		t("messages.cancelled"),
		t("messages.cancelFailed")
	);
}

export function useDeleteTask() {
	const { t } = useTranslation("tasks");
	return useTasksMutation(
		(id: string) => tasksService.remove(id),
		t("messages.deleted"),
		t("messages.deleteFailed")
	);
}
